package com.karawek.core.platform

import kotlinx.browser.window
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

private class WebSecureStorage(
    name: String,
) : SecureStorage {
    private val prefix = "$name:"

    override fun set(key: String, stringValue: String): Boolean = runCatching {
        window.localStorage.setItem(prefixed(key), stringValue)
    }.isSuccess

    override fun set(key: String, intValue: Int): Boolean = set(key, intValue.toString())
    override fun set(key: String, longValue: Long): Boolean = set(key, longValue.toString())
    override fun set(key: String, floatValue: Float): Boolean = set(key, floatValue.toString())
    override fun set(key: String, doubleValue: Double): Boolean = set(key, doubleValue.toString())
    override fun set(key: String, boolValue: Boolean): Boolean = set(key, boolValue.toString())

    @OptIn(ExperimentalEncodingApi::class)
    override fun set(key: String, dataValue: ByteArray): Boolean =
        set(key, Base64.encode(dataValue))

    override fun string(forKey: String): String? = window.localStorage.getItem(prefixed(forKey))
    override fun int(forKey: String): Int? = string(forKey)?.toIntOrNull()
    override fun long(forKey: String): Long? = string(forKey)?.toLongOrNull()
    override fun float(forKey: String): Float? = string(forKey)?.toFloatOrNull()
    override fun double(forKey: String): Double? = string(forKey)?.toDoubleOrNull()
    override fun bool(forKey: String): Boolean? = string(forKey)?.let {
        when (it.lowercase()) {
            "true" -> true
            "false" -> false
            else -> null
        }
    }

    @OptIn(ExperimentalEncodingApi::class)
    override fun data(forKey: String): ByteArray? =
        string(forKey)?.let { runCatching { Base64.decode(it) }.getOrNull() }

    override fun deleteObject(forKey: String): Boolean = runCatching {
        window.localStorage.removeItem(prefixed(forKey))
    }.isSuccess

    override fun clear(): Boolean = runCatching {
        val keys = (0 until window.localStorage.length).mapNotNull { idx ->
            window.localStorage.key(idx)
        }.filter { it.startsWith(prefix) }
        keys.forEach { window.localStorage.removeItem(it) }
    }.isSuccess

    private fun prefixed(key: String) = prefix + key
}

internal actual fun createSecureStorage(context: PlatformContext, name: String): SecureStorage =
    WebSecureStorage(name = name)
